/**
 * @file useShopSeoSettings.ts
 * @description Общие SEO-настройки магазина для вкладки «Основное»
 * (phase19.16, партия Р3в, v0.19.64; SB145, SEO16; контракт
 * r3v-api-contract §5).
 *
 * Один источник правды — ответ сервера: после сохранения, «Вернуть прежнее»,
 * «Применить» и «Отбросить» кладутся новые настройки целиком (версия,
 * отметки «изменено», плашка неопубликованных правок), а не правятся руками.
 * Все записи шлют `expectedVersion`; 409 «изменено в другом окне» —
 * перечитать и сказать об этом тостом. Новое значение уходит и в общий
 * снимок вкладки (красная плашка «Сайт закрыт от поисковиков»).
 */

import { useCallback, useEffect, useState } from "react";

import { useNotify } from "../../components/ui/NotifyProvider";
import { shopSeo, type ShopSeoPatch, type ShopSeoSettingsDto, type ShopSeoVersionBody, type ShopSeoWriteOptions, type ShopSeoWriteResult } from "../../lib/api-site-seo";
import { errorCode, seoErrorText } from "../../lib/site-seo/seo-errors";
import { setSeoSettingsSnapshot } from "../../lib/site-seo/seo-settings-store";

export interface ShopSeoApi {
  settings: ShopSeoSettingsDto | null;
  loading: boolean;
  loadError: string | null;
  reload: () => Promise<void>;
  /** Сохранить поля; ошибка пробрасывается вызывающему (поле, стоп-лист), 409 — перечитать. */
  save: (patch: Omit<ShopSeoPatch, "expectedVersion">) => Promise<ShopSeoWriteResult>;
  /** «Вернуть прежнее» по строке журнала; `acknowledgeChecks` — после галочки стоп-листа. */
  revert: (historyId: string, options?: ShopSeoWriteOptions) => Promise<ShopSeoWriteResult>;
  /** `confirm` — «закрыть», если среди правок «закрыт от поисковиков» (SB145); `acknowledgeChecks` — SB125. */
  applyPending: (options?: ShopSeoWriteOptions) => Promise<ShopSeoWriteResult>;
  discardPending: () => Promise<ShopSeoWriteResult>;
}

export function useShopSeoSettings(): ShopSeoApi {
  const notify = useNotify();
  const [settings, setSettings] = useState<ShopSeoSettingsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const apply = useCallback((next: ShopSeoSettingsDto) => {
    setSettings(next);
    setSeoSettingsSnapshot(next);
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      apply(await shopSeo.get());
    } catch (err) {
      setLoadError(seoErrorText(err, "Не удалось загрузить настройки SEO."));
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** Общий путь записи: версия, новый ответ, 409 — перечитать и пробросить. */
  const write = useCallback(
    async (run: (body: ShopSeoVersionBody) => Promise<ShopSeoWriteResult>) => {
      if (!settings) throw new Error("Настройки SEO ещё не загружены");
      try {
        const result = await run({ expectedVersion: settings.version });
        apply(result.settings);
        return result;
      } catch (err) {
        if (errorCode(err) === "site_seo_version_conflict") {
          notify.warning(seoErrorText(err, ""));
          await reload();
        }
        throw err;
      }
    },
    [apply, notify, reload, settings],
  );

  const save = useCallback((patch: Omit<ShopSeoPatch, "expectedVersion">) => write((body) => shopSeo.save({ ...body, ...patch })), [write]);
  const revert = useCallback(
    (historyId: string, options: ShopSeoWriteOptions = {}) => write((body) => shopSeo.revert(historyId, { ...body, ...options })),
    [write],
  );
  const applyPending = useCallback((options: ShopSeoWriteOptions = {}) => write((body) => shopSeo.applyPending({ ...body, ...options })), [write]);
  const discardPending = useCallback(() => write((body) => shopSeo.discardPending(body)), [write]);

  return { settings, loading, loadError, reload, save, revert, applyPending, discardPending };
}
